import type {
  HealthPlanDetail,
  HealthPlanListItem,
  PatientListItem,
  User,
} from '@/api/model';

// 角色 ID 常量（与 server/internal/middleware/auth.go 一致）
export const ROLE_SUPER_ADMIN = 'role-super-admin';
export const ROLE_HEALTH_MGR = 'role-health-mgr';
export const ROLE_SPEC_DOCTOR = 'role-spec-doctor';
export const ROLE_OPERATOR = 'role-operator';

/** 判断用户是否拥有任一指定角色 */
export const hasRole = (
  user: User | undefined | null,
  ...ids: string[]
): boolean => !!user?.roles?.some((r) => ids.includes(r.id));

/** 当前用户是否可对患者执行「加入/移出我的患者」操作（健管师 / 专科医生） */
export const canManageMyPatient = (user: User | undefined | null): boolean =>
  hasRole(user, ROLE_HEALTH_MGR, ROLE_SPEC_DOCTOR);

/**
 * 当前用户能否将指定患者「加入我的患者」。
 * - 专科医生不受归属影响，任何患者都可以加入
 * - 健管师只能认领尚未分配健管师的患者（后端 CAS 会再次校验，前端这里避免点了必失败的按钮）
 */
export const canClaimPatient = (
  user: User | undefined | null,
  patient: Pick<PatientListItem, 'healthManagerId'>,
): boolean => {
  if (hasRole(user, ROLE_SPEC_DOCTOR)) return true;
  if (hasRole(user, ROLE_HEALTH_MGR)) return patient.healthManagerId == null;
  return false;
};

// --- 健康方案权限判定（与 server 端 health_plan_acl.go 对齐） ---

type PlanLike = Pick<
  HealthPlanDetail | HealthPlanListItem,
  'ownerId' | 'inConsultation'
>;

/** 当前用户是否为方案 owner */
export const isOwnerOf = (
  user: User | undefined | null,
  plan: PlanLike | undefined | null,
): boolean => !!plan && !!plan.ownerId && !!user && plan.ownerId === user.id;

/**
 * 点「编辑」按钮是否可用：
 * - 必须是健管师 / 专科医生，超级管理员仅可查看
 * - in_consultation=true 时：仅 owner 本人可（其他人会被后端拒绝）
 * - in_consultation=false 时：健管师还要受「不抢占」规则限制（只看本人或未认领）
 */
export const canClaimEdit = (
  user: User | undefined | null,
  plan: PlanLike | undefined | null,
  patient: Pick<PatientListItem, 'healthManagerId'>,
): boolean => {
  if (!user || !plan) return false;
  if (hasRole(user, ROLE_SUPER_ADMIN)) return false;
  if (!canManageMyPatient(user)) return false;
  if (plan.inConsultation) {
    return isOwnerOf(user, plan);
  }
  // 健管师抢占规则：仅本人或未认领患者可
  if (hasRole(user, ROLE_HEALTH_MGR)) {
    if (
      patient.healthManagerId == null ||
      patient.healthManagerId === user.id
    ) {
      return true;
    }
    // 不是该患者的健管师 → 只允许专科医生身份继续判断（双角色账号）
    return hasRole(user, ROLE_SPEC_DOCTOR);
  }
  return hasRole(user, ROLE_SPEC_DOCTOR);
};

/** 当前用户能否发起会诊（仅健管师 + 是当前方案 owner 或方案无主 + 已认领该患者 + 未在会诊中） */
export const canConsultTransfer = (
  user: User | undefined | null,
  plan: PlanLike | undefined | null,
  patient: Pick<PatientListItem, 'healthManagerId'>,
): boolean => {
  if (!user || !plan) return false;
  if (hasRole(user, ROLE_SUPER_ADMIN)) return false;
  if (!hasRole(user, ROLE_HEALTH_MGR)) return false;
  if (plan.inConsultation) return false;
  if (plan.ownerId != null && plan.ownerId !== user.id) return false;
  return patient.healthManagerId === user.id;
};

/** 超级管理员能否从详情页协调转交专科会诊 */
export const canCoordinateConsultTransfer = (
  user: User | undefined | null,
  plan: PlanLike | undefined | null,
): boolean => !!plan && hasRole(user, ROLE_SUPER_ADMIN) && !plan.inConsultation;

/** 当前用户能否点「发布」（owner + 状态 draft） */
export const canPublish = (
  user: User | undefined | null,
  plan: (PlanLike & { status: string }) | undefined | null,
): boolean => !!plan && plan.status === 'draft' && canSavePlan(user, plan);

/** 当前用户能否保存（健管师或专科医生且为 owner） */
export const canSavePlan = (
  user: User | undefined | null,
  plan: PlanLike | undefined | null,
): boolean =>
  !hasRole(user, ROLE_SUPER_ADMIN) &&
  isOwnerOf(user, plan) &&
  hasRole(user, ROLE_HEALTH_MGR, ROLE_SPEC_DOCTOR);

/** 当前用户能否修改健康方案中的用药处方 */
export const canEditPlanMedication = (user: User | undefined | null): boolean =>
  !hasRole(user, ROLE_SUPER_ADMIN) && hasRole(user, ROLE_SPEC_DOCTOR);

/** 当前用户能否删除方案（健管师或专科医生且为 owner） */
export const canDeletePlan = (
  user: User | undefined | null,
  plan: PlanLike | undefined | null,
): boolean => canSavePlan(user, plan);
